import { DbEnum } from "../generated/prisma-enums";

/**
 * Customer name resolution helper — shared between backend (conversations
 * thread-message mapper) and frontend (legacy inbox bubble + chat list).
 *
 * Each deployment type has its own strategy for what to display when the
 * stored topic / explicit display name is unavailable: chat-style channels
 * (WhatsApp / Telegram / Messenger / Slack / Teams) prefer the customer's
 * stored display name with a raw-id guard (Slack and similar persist the
 * raw user id as displayName until enrichment); email-style channels
 * (Gmail / Outlook / IMAP / SMTP) fall back to the email address as the
 * label since there's no friendlier identifier in the customer-identity
 * payload.
 */

/**
 * Minimal structural type for the customer identity payload consumed by
 * the per-channel adapters. Backend mapper passes a Prisma-shaped subset;
 * frontend passes the same fields off the chat session view. Kept as a
 * structural duck type so neither layer has to import the other's typed
 * payload.
 */
export type CustomerNameIdentity =
  | {
      identifier: string;
      customer?: { displayName?: string | null };
    }
  | null
  | undefined;

/**
 * Returns a meaningful human name only — guards against raw IDs stored
 * as display names. Slack / Teams / similar persist the upstream user id
 * as displayName until enrichment runs; in those windows we want the
 * generic fallback rather than rendering a raw id.
 */
function resolveDisplayName(identity: CustomerNameIdentity): string | undefined {
  const name = identity?.customer?.displayName;
  const identifier = identity?.identifier;
  return name && name !== identifier ? name : undefined;
}

/**
 * Per-channel adapters: return the resolved customer name or undefined.
 * `undefined` means "no meaningful name available — caller picks the
 * generic fallback" (e.g. "Unknown" / "New conversation").
 */
const CUSTOMER_NAME_ADAPTERS: Partial<
  Record<DbEnum.DeploymentType, (identity: CustomerNameIdentity) => string | undefined>
> = {
  // Chat channels: sender display name is the primary contact identifier.
  [DbEnum.DeploymentType.WHATSAPP]: resolveDisplayName,
  [DbEnum.DeploymentType.TELEGRAM]: resolveDisplayName,
  [DbEnum.DeploymentType.FB_MESSENGER]: resolveDisplayName,
  // Instagram's messaging webhook carries only the customer's IGSID, never a
  // username — so this resolves a name only once the Customer record is enriched
  // from elsewhere, and falls back to the generic label until then. Listed anyway
  // so Instagram behaves like every other chat channel the moment one exists.
  [DbEnum.DeploymentType.INSTAGRAM]: resolveDisplayName,

  // Email channels: identifier IS the email address — show it as the label.
  [DbEnum.DeploymentType.GMAIL]: (identity) => identity?.identifier ?? undefined,
  [DbEnum.DeploymentType.OUTLOOK]: (identity) => identity?.identifier ?? undefined,
  [DbEnum.DeploymentType.IMAP]: (identity) => identity?.identifier ?? undefined,
  [DbEnum.DeploymentType.SMTP]: (identity) => identity?.identifier ?? undefined,

  // Slack / Teams: enrichment may lag the inbound webhook — guard against
  // raw user ids leaking through as displayName during the gap.
  [DbEnum.DeploymentType.SLACK]: resolveDisplayName,
  [DbEnum.DeploymentType.TEAMS]: resolveDisplayName
};

/**
 * Resolves the customer's display name for a given deployment type.
 * Returns `undefined` when no meaningful name is available — callers
 * pick the contextual fallback (chat list uses "New conversation",
 * bubble headers fall back to a generic role label, etc.).
 *
 * Unknown / future deployment types fall through to `resolveDisplayName`
 * so a new channel without a registered adapter still surfaces a usable
 * name when the upstream identity carries one.
 */
export function resolveCustomerName(
  deploymentType: DbEnum.DeploymentType | string | null | undefined,
  customerIdentity: CustomerNameIdentity
): string | undefined {
  const adapter = deploymentType
    ? CUSTOMER_NAME_ADAPTERS[deploymentType as DbEnum.DeploymentType]
    : undefined;

  if (adapter !== undefined) return adapter(customerIdentity);
  return resolveDisplayName(customerIdentity);
}
